<template>
    <Modal
        :opened="isOpen"
        :title="texts.title"
        :dissmisable="true"
        class="CreatePresetModal"
        @close="closeModal"
    >
        <template #body>
            <div class="CreatePresetModalBody">
                <div
                    v-if="type === 'save'"
                    class="CheckContainer"
                >
                    <WRadio
                        :value="saveVariant"
                        modelValue="create"
                        name="create"
                        label="Create new"
                        @input="saveVariant = 'create'"
                    />
                    <WRadio
                        :value="saveVariant"
                        modelValue="update"
                        name="update"
                        label="Update existing"
                        @input="saveVariant = 'update'"
                    />
                </div>
                <WText
                    :size="16"
                    color="gray01"
                    class="NewPresetWarning"
                >
                    {{ texts.body }}
                </WText>
                <WText
                    v-if="showNameInput"
                    :size="12"
                    color="gray02"
                    class="PresetNameLabel"
                >
                    {{ $t('filters.presets.create.modal.name.label') }}
                </WText>
                <WInput
                    v-if="showNameInput"
                    v-model="presetName"
                    type="text"
                    name="PresetName"
                    :placeholder="$t('filters.presets.create.modal.name.placeholder')"
                />
                <WSelect
                    v-if="isUpdate"
                    v-model="presetToUpdate"
                    name="presetToUpdate"
                    :placeholder="$t('filters.presets.update.modal.placeholder')"
                    :options="presetsOptions"
                />
                <div class="CreatePresetButtons">
                    <WButton
                        outline
                        color="accent-blue"
                        @click="closeModal"
                    >
                        {{ $t('filters.presets.create.modal.cancel') }}
                    </WButton>
                    <WButton
                        :disabled="acceptDisabled"
                        @click="handleAccept"
                    >
                        {{ $t('filters.presets.create.modal.accept') }}
                    </WButton>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script>
import VueTypes from 'vue-types'
import { mapGetters } from 'vuex'
import { Api, apiRequest } from '@/api'
import { Modal } from '@/components'

export default {
    name: 'SavePresetModal',
    components: { Modal },
    props: {
        isOpen: VueTypes.bool,
        type: VueTypes.string,
        selectedPreset: VueTypes.object,
        presetsOptions: VueTypes.array,
    },
    data () {
        return {
            presetName: '',
            saveVariant: 'create',
            presetToUpdate: '',
        }
    },
    computed: {
        ...mapGetters({
            relatedFilters: 'filters/relatedFilters',
        }),
        texts () {
            const variants = {
                save: {
                    title: this.$t('filters.presets.create.modal.title'),
                    body: this.$t(`filters.presets.${this.isUpdate ? 'update' : 'create'}.modal.warning`),
                },
                edit: {
                    title: this.$t('filters.presets.edit.modal.title'),
                    body: this.$t('filters.presets.edit.modal.warning', { searchLabel: this.selectedPreset.label }),
                },
            }
            return variants[this.type]
        },
        showNameInput () {
            return this.type === 'edit' || this.saveVariant === 'create'
        },
        isUpdate () {
            return this.type === 'save' && this.saveVariant === 'update'
        },
        acceptDisabled () {
            const variants = {
                save: this.isUpdate ? !this.presetToUpdate : !this.presetName,
                edit: !this.presetName || this.presetName === this.selectedPreset.label,
            }
            return variants[this.type]
        },
    },
    watch: {
        isOpen (newIsOpen) {
            if (newIsOpen) {
                this.presetName = this.type === 'edit' ? this.selectedPreset.label : ''
            }
        },
    },
    methods: {
        closeModal () {
            this.presetName = ''
            this.$emit('close')
        },
        createPreset () {
            this.$emit('createPreset', this.presetName)
            this.closeModal()
        },
        updatePreset () {
            this.$emit('updatePreset', this.presetToUpdate)
            this.closeModal()
        },
        editPreset () {
            apiRequest(Api().presets.updateById(this.selectedPreset.key, {
                name: this.presetName,
            }))
                .then(() => {
                    this.$emit('nameEdited', this.selectedPreset.key)
                    this.closeModal()
                })
        },
        handleAccept () {
            const variants = {
                save: this.isUpdate ? this.updatePreset : this.createPreset,
                edit: this.editPreset,
            }
            return variants[this.type]()
        },
    },
}
</script>

<style lang="scss" scoped>
.CreatePresetModal {
    &::v-deep .Dialog {
        overflow: visible;
    }
    .CreatePresetModalBody {
        .CheckContainer {
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 1;
            gap: 15px;
            margin-bottom: 20px;
        }
        .NewPresetWarning {
            margin-bottom: 15px;
        }
        .PresetNameLabel {
            margin-bottom: 2px;
        }
        .CreatePresetButtons {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            margin-top: 30px;
        }
    }
}
</style>
